import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { SPORTS_LIST, getSportsByCategory, searchSports } from '../data/sportsData';

const { FiSearch, FiChevronDown, FiX } = FiIcons;

function SportSelector({ value, onChange, className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'category'

  const sportsByCategory = useMemo(() => getSportsByCategory(), []);
  
  const filteredSports = useMemo(() => {
    if (!searchQuery) return SPORTS_LIST;
    return searchSports(searchQuery);
  }, [searchQuery]);

  const handleSelect = (sport) => {
    onChange(sport.value);
    setIsOpen(false);
    setSearchQuery('');
  };

  const selectedSport = SPORTS_LIST.find(s => s.value === value);

  return (
    <div className={`relative ${className}`}>
      {/* Selected Value Display */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white flex items-center justify-between"
      >
        <span className={selectedSport ? 'text-gray-800' : 'text-gray-400'}>
          {selectedSport ? selectedSport.label : 'Select a sport...'}
        </span>
        <SafeIcon 
          icon={isOpen ? FiX : FiChevronDown} 
          className="w-4 h-4 text-gray-400"
        />
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-hidden"
          >
            {/* Search Bar */}
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <SafeIcon 
                  icon={FiSearch} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                />
                <input
                  type="text"
                  placeholder="Search sports..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  autoFocus
                />
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center space-x-2 px-3 py-2 border-b border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => setViewMode('all')}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  viewMode === 'all'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                All Sports
              </button>
              <button
                type="button"
                onClick={() => setViewMode('category')}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  viewMode === 'category'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                By Category
              </button>
            </div>

            {/* Sports List */}
            <div className="overflow-y-auto max-h-64">
              {viewMode === 'all' ? (
                // All Sports View
                <div>
                  {filteredSports.length > 0 ? (
                    filteredSports.map((sport, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelect(sport)}
                        className="w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors flex items-center justify-between group"
                      >
                        <span className="text-sm text-gray-800">{sport.label}</span>
                        <span className="text-xs text-gray-400 group-hover:text-gray-600">
                          {sport.category}
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-8 text-center text-gray-500 text-sm">
                      No sports found
                    </div>
                  )}
                </div>
              ) : (
                // Category View
                <div>
                  {Object.entries(sportsByCategory).map(([category, sports]) => (
                    <div key={category}>
                      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                          {category}
                        </h4>
                      </div>
                      {sports.map((sport, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleSelect(sport)}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="text-sm text-gray-800">{sport.label}</span>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Popular Sports Quick Select */}
            {!searchQuery && (
              <div className="border-t border-gray-200 p-3 bg-gray-50">
                <p className="text-xs font-medium text-gray-600 mb-2">Popular Sports:</p>
                <div className="flex flex-wrap gap-2">
                  {['Basketball', 'Football', 'Soccer', 'Baseball', 'Track & Field', 'Swimming'].map((sportName) => {
                    const sport = SPORTS_LIST.find(s => s.value === sportName);
                    return (
                      <button
                        key={sportName}
                        type="button"
                        onClick={() => handleSelect(sport)}
                        className="px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-700 hover:bg-primary hover:text-white hover:border-primary transition-colors"
                      >
                        {sportName}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}

export default SportSelector;